import { useCallback, useEffect, useId, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { EngagementFieldAvailability } from '@/api/generated/model';

export type EngagementReadyState = 'live' | 'partial' | 'not-ready';

const DEFAULT_HINT: Record<Exclude<EngagementReadyState, 'live'>, string> = {
  partial:
    'Some data for this date range is incomplete. Live figures still show; missing sources are left blank instead of inventing zeros.',
  'not-ready':
    'This figure is not available yet for the selected date range.',
};

export function engagementReadyState(
  availability?: EngagementFieldAvailability,
): EngagementReadyState {
  if (availability == null || availability === 'AVAILABLE') return 'live';
  if (availability === 'INCOMPLETE') return 'partial';
  return 'not-ready';
}

export function NotReadyBadge({
  state = 'not-ready',
  className,
  label,
  hint,
}: {
  state?: EngagementReadyState;
  className?: string;
  label?: string;
  /** Hover explanation. Falls back to a default Partial / Not ready reason. */
  hint?: string;
}) {
  if (state === 'live') return null;
  const text = label ?? (state === 'partial' ? 'Partial' : 'Not ready');
  const tooltip = hint?.trim() || DEFAULT_HINT[state];

  return (
    <HoverHint text={tooltip} label={`${text}. ${tooltip}`}>
      <Badge
        variant="outline"
        className={cn(
          'cursor-help text-[10px] font-medium uppercase tracking-wide',
          state === 'partial'
            ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
            : 'border-sky-500/40 bg-sky-500/10 text-sky-300',
          className,
        )}
      >
        {text}
      </Badge>
    </HoverHint>
  );
}

export function NotReadyNote({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn('text-[11px] leading-4 text-sky-400/90', className)}>{children}</p>;
}

/** Card-body placeholder so a filter change does not unmount the page shell. */
export function EngagementBusy({
  label = 'Loading…',
  className,
}: {
  label?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex h-48 items-center justify-center gap-2 text-sm text-muted-foreground',
        className,
      )}
    >
      <Loader2 className="size-4 animate-spin" aria-hidden />
      {label}
    </div>
  );
}

/** Portal tooltip on hover/focus — shared by readiness badges and KPI labels. */
export function HoverHint({
  text,
  label,
  children,
  className,
}: {
  text: string;
  label: string;
  children: ReactNode;
  className?: string;
}) {
  const tipId = useId();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number; placement: 'above' | 'below' }>({
    x: 0,
    y: 0,
    placement: 'above',
  });

  const place = useCallback((el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    // Keep the portal tip inside the viewport (top-row KPIs clip when forced above).
    const tipHalf = 160;
    const x = Math.min(
      Math.max(rect.left + rect.width / 2, tipHalf + 8),
      window.innerWidth - tipHalf - 8,
    );
    const estimatedTipHeight = Math.min(160, 48 + Math.ceil(text.length / 36) * 16);
    const placeBelow = rect.top < estimatedTipHeight + 16;
    setPos({
      x,
      y: placeBelow ? rect.bottom : rect.top,
      placement: placeBelow ? 'below' : 'above',
    });
  }, [text]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  return (
    <>
      <span
        className={cn('inline-flex outline-none', className)}
        aria-label={label}
        aria-describedby={open ? tipId : undefined}
        onMouseEnter={(event) => {
          place(event.currentTarget);
          setOpen(true);
        }}
        onMouseMove={(event) => place(event.currentTarget)}
        onMouseLeave={() => setOpen(false)}
        onFocus={(event) => {
          place(event.currentTarget);
          setOpen(true);
        }}
        onBlur={() => setOpen(false)}
        tabIndex={0}
      >
        {children}
      </span>
      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              id={tipId}
              role="tooltip"
              className={cn(
                'pointer-events-none fixed z-[100] max-w-[320px] -translate-x-1/2 rounded-md px-3 py-2 text-left shadow-lg',
                pos.placement === 'below'
                  ? 'translate-y-2'
                  : '-translate-y-[calc(100%+8px)]',
              )}
              style={{ left: pos.x, top: pos.y, backgroundColor: '#1e2a44' }}
            >
              <p className="text-[12px] leading-4 text-white">{text}</p>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
